import { getPartById, parts } from '../catalog/part-catalog'
import { PINGPONG_CUBE_PART_ID } from '../domain/motor-control'
import { useStudioStore } from '../store/useStudioStore'
import { PartVisual } from './PartVisual'

const connectorLabels: Record<string, string> = {
  'motor-male': 'C1-02 모터 수형',
  'motor-male-wheel': 'WL-04 모터 수형',
  'motor-male-fork': 'C1-03 모터 수형',
  'motor-female-front': '큐브 옆면 모터 암형',
  'cross-male-a': 'C2-13 일반 십자 수형 A',
  'cross-male-b': 'C2-13 일반 십자 수형 B',
  'cross-male-pair-left': 'C3-02 일반 십자 수형 좌측',
  'cross-male-pair-right': 'C3-02 일반 십자 수형 우측',
  'cross-male-single': 'C3-02 일반 십자 수형 단독',
  'cross-male-fork': 'C1-03 일반 십자 수형',
  'cross-female-right': '큐브 일반 십자 암형 우측',
  'cross-female-left': '큐브 일반 십자 암형 좌측',
  'cross-female-top': '큐브 일반 십자 암형 상단',
  'cross-female-back': '큐브 일반 십자 암형 후면',
  'cross-female-bottom': '큐브 일반 십자 암형 하단',
}

export function Inspector() {
  const selectedPartId = useStudioStore((state) => state.selectedPartId)
  const selectedInstanceId = useStudioStore((state) => state.selectedInstanceId)
  const placedParts = useStudioStore((state) => state.placedParts)
  const placedPartCount = placedParts.length
  const placedPart = useStudioStore((state) => state.placedParts.find((item) => item.instanceId === selectedInstanceId))
  const connections = useStudioStore((state) => state.connections)
  const setSelectedPosition = useStudioStore((state) => state.setSelectedPosition)
  const addSelectedPart = useStudioStore((state) => state.addSelectedPart)
  const duplicateSelectedPart = useStudioStore((state) => state.duplicateSelectedPart)
  const removeSelectedPart = useStudioStore((state) => state.removeSelectedPart)
  const clearAssembly = useStudioStore((state) => state.clearAssembly)
  const selectedConnections = selectedInstanceId
    ? connections.filter((connection) =>
        connection.fromPartId === selectedInstanceId || connection.toPartId === selectedInstanceId)
    : []
  const part = getPartById(placedPart?.partId ?? selectedPartId) ?? parts[0]
  const selectedCubeNumber = placedPart?.partId === PINGPONG_CUBE_PART_ID
    ? placedParts
        .filter((item) => item.partId === PINGPONG_CUBE_PART_ID)
        .findIndex((item) => item.instanceId === placedPart.instanceId) + 1
    : null
  const specs = [
    ['질량', `${Math.round(part.physics.mass * 1000)} g`],
    ['마찰 계수', part.physics.friction.toFixed(2)],
    ['커넥터', `${part.connectors.length}개`],
    ['최대 토크', part.motor ? `${part.motor.maxTorque} N·m` : '—'],
    ['무부하 회전수', part.motor ? `${part.motor.maxRpm} rpm` : '—'],
  ]

  return (
    <aside className="inspector">
      <div className="panel-heading">
        <div><strong>부품 속성</strong><small>선택한 객체</small></div>
      </div>
      <div className="selected-object" aria-label="부품 요약">
        <PartVisual part={part} />
        <div>
          <strong>{part.id === PINGPONG_CUBE_PART_ID ? '핑퐁 큐브' : `${part.name} 모델`}</strong>
          {selectedCubeNumber ? <span>ID : {selectedCubeNumber}</span> : null}
        </div>
      </div>

      <div className="inspector-actions" aria-label="부품 작업">
        <button
          className="primary"
          type="button"
          aria-label="선택 부품 추가"
          onClick={addSelectedPart}
        >
          + 선택 부품 추가
        </button>
        <div>
          <button type="button" onClick={duplicateSelectedPart} disabled={!selectedInstanceId}>복제</button>
          <button className="danger" type="button" onClick={removeSelectedPart} disabled={!selectedInstanceId}>삭제</button>
        </div>
      </div>

      <div className="inspector-accordions">
        <details open>
          <summary>부품 정보 <span>{part.subtitle}</span></summary>
          <dl className="spec-list">
            {specs.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
          </dl>
        </details>

        <details open>
          <summary>위치·회전 <span>{selectedInstanceId ? '선택됨' : '미선택'}</span></summary>
          {placedPart ? (
            <>
              <p className="coordinate-note"><b>화면 좌표</b><span>X 좌우 · Y 앞뒤 · Z 높이</span></p>
              <dl className="spec-list transform-specs">
                <div><dt>X · 좌우</dt><dd><input key={`x-${placedPart.position.x}`} aria-label="X 좌우 위치" type="number" step="0.05" defaultValue={placedPart.position.x} onBlur={(event) => setSelectedPosition('x', event.currentTarget.valueAsNumber)} /><small>{Math.round(placedPart.position.x * 50)} mm</small></dd></div>
                <div><dt>Y · 앞뒤</dt><dd><input key={`z-${placedPart.position.z}`} aria-label="Y 앞뒤 위치" type="number" step="0.05" defaultValue={placedPart.position.z} onBlur={(event) => setSelectedPosition('z', event.currentTarget.valueAsNumber)} /><small>{Math.round(placedPart.position.z * 50)} mm</small></dd></div>
                <div><dt>Z · 높이</dt><dd><input key={`y-${placedPart.position.y}`} aria-label="Z 높이 위치" type="number" step="0.05" defaultValue={placedPart.position.y} onBlur={(event) => setSelectedPosition('y', event.currentTarget.valueAsNumber)} /><small>{Math.round(placedPart.position.y * 50)} mm</small></dd></div>
                <div><dt>X 회전</dt><dd>{Math.round(placedPart.rotation.x * 180 / Math.PI)}°</dd></div>
                <div><dt>Y 회전</dt><dd>{Math.round(placedPart.rotation.y * 180 / Math.PI)}°</dd></div>
                <div><dt>Z 회전</dt><dd>{Math.round(placedPart.rotation.z * 180 / Math.PI)}°</dd></div>
              </dl>
            </>
          ) : <p className="accordion-empty">작업공간에서 부품을 선택하세요.</p>}
        </details>

        <details open>
          <summary>연결 관계 <span>{selectedConnections.length}개</span></summary>
          {selectedConnections.length ? (
            <div className="connection-list">
              {selectedConnections.map((connection) => (
                <div key={connection.id}>
                  <strong>{connection.jointType === 'fixed' ? '고정 연결' : '회전 연결'}</strong>
                  <span>{connectorLabels[connection.fromConnectorId] ?? connection.fromConnectorId} ↔ {connectorLabels[connection.toConnectorId] ?? connection.toConnectorId}</span>
                </div>
              ))}
            </div>
          ) : <p className="accordion-empty">연결된 커넥터가 없습니다.</p>}
        </details>

      </div>

      <div className="inspector-clear-footer">
        <span><strong>전체 조립 비우기</strong><small>현재 배치된 부품 {placedPartCount}개</small></span>
        <button className="clear-all" type="button" onClick={clearAssembly} disabled={!placedPartCount}>모두 없애기</button>
      </div>
    </aside>
  )
}
